import { CellData, CellUtil, ComponentData } from '@ddt-web/data-service';
import * as get from 'lodash/get';

import { EntityFlags } from './entity-flags';

export class PartnerPortfolioFlagUtils {

	/**
	 * Checks if flags has warnings
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if portfolio has warnings, false otherwise
	 */
	public static hasWarnings(portfolioFlags: EntityFlags): boolean {
		return PartnerPortfolioFlagUtils.hasAssetClassSubstitutions(portfolioFlags);
	}

	/**
	 * Checks if flags have errors
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if portfolio has errors, false otherwise
	 */
	public static hasErrors(portfolioFlags: EntityFlags): boolean {
		return PartnerPortfolioFlagUtils.hasUnsupportedSleeves(portfolioFlags) || PartnerPortfolioFlagUtils.hasUnsupportedSecurities(portfolioFlags);
	}

	/**
	 * Checks if the portfolio has reallocated securities based on the provided flags. After the user has made their selection.
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if portfolio has reallocated securities, false otherwise
	 */
	public static hasReallocatedSecurities(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsUnmatchedSecurities && portfolioFlags.originContainsReallocatedSecurities;
	}

	/**
	 * Checks if the portfolio has cash reallocation based on the provided flags. After the user has made their selection.
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if portfolio has cash reallocation, false otherwise
	 */
	public static hasCashSubstitutions(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsUnmatchedSecurities && !portfolioFlags.originContainsReallocatedSecurities;
	}


	/**
	 * Checks if the portfolio has asset class substitutions based on the provided flags.
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if portfolio has asset class substitutions, false otherwise
	 */
	public static hasAssetClassSubstitutions(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsAssetClassSubstitutions;
	}

	/**
	 * Checks if the portfolio has fixed income substitutions based on the provided flags.
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if portfolio has fixed income substitutions, false otherwise
	 */
	public static hasBondSubstitutions(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsFixedIncomeSubstitutions;
	}

	/**
	 * Check for unsupported SMA - Seperately managed account
	 *
	 * @param {EntityFlags} portfolioFlags
	 * @returns {boolean}
	 * @memberof PartnerPortfolioFlagUtils
	 */
	public static hasUnsupportedSeparateManagedAccount(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsUnsupportedSMA;
	}

	/**
	 * Check for unsupported managed model
	 *
	 * @param {EntityFlags} portfolioFlags
	 * @returns {boolean}
	 * @memberof PartnerPortfolioFlagUtils
	 */
	public static hasUnsupportedManagedModel(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsUnsupportedManagedModel;
	}

	/**
	 * Check for APM Sleeve with unsupported securities
	 *
	 * @param {EntityFlags} portfolioFlags
	 * @returns {boolean}
	 * @memberof PartnerPortfolioFlagUtils
	 */
	public static hasUnsupportedAPMSleeve(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originSleeveContainsUnmatchedSecurities;
	}

	/**
	 * Check for unsupported sleeves
	 *
	 * @param {EntityFlags} portfolioFlags
	 * @returns {boolean}
	 * @memberof PartnerPortfolioFlagUtils
	 */
	public static hasUnsupportedSleeves(portfolioFlags: EntityFlags): boolean {
		return PartnerPortfolioFlagUtils.hasUnsupportedManagedModel(portfolioFlags) ||
			PartnerPortfolioFlagUtils.hasUnsupportedSeparateManagedAccount(portfolioFlags) ||
			PartnerPortfolioFlagUtils.hasUnsupportedAPMSleeve(portfolioFlags);
	}

	/**
	 * Check for unsupported securities
	 *
	 * @param {EntityFlags} portfolioFlags
	 * @returns {boolean}
	 * @memberof PartnerPortfolioFlagUtils
	 */
	public static hasUnsupportedSecurities(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsUnmatchedSecurities;
	}

	/**
	 * Checks if the third party model is not editable
	 *
	 * @portfolioFlags object with alert flags
	 * @return true if user has no permissions to edit
	 */
	public static isViewOnly(portfolioFlags: EntityFlags): boolean {
		return portfolioFlags && portfolioFlags.originContainsEditableModel === false;
	}
	/**
	 * Takes a ComponentData object and returns an array of EntityFlags
	 * @param data the ComponentData
	 */
	public static populatePartnerPortfolioFlags(data: ComponentData): EntityFlags[] {
		const cells: CellData[][] = get(data.getTabById('portfolio.flags'), 'tableData[0].cells', []);
		return cells.map(cellData => (get(CellUtil.getById(cellData, 'portfolioFlags'), 'rawValue', {})));
	}

	/**
	 * Returns true if flags have warnings flags (originContainsAssetClassSubstitutions) or if isProcessed is true.
	 * hasWarnings is present where a clean portfolio has asset class substitutions.
	 * If isProcessed is true, it means that portfolio was dirty and it was fixed.
	 *
	 * @param portfolioFlags
	 */
	public static isModelDirty(portfolioFlags: EntityFlags): boolean {
		return PartnerPortfolioFlagUtils.hasWarnings(portfolioFlags) || get(portfolioFlags, 'isProcessed', false);
	}
}
